// DMGridContainer.jsx
import React, { useEffect, useState, useRef, Component, useMemo, useCallback } from "react";
import { cloneDeep } from "lodash";
import { DotLoader } from "react-spinners";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import Select, { components } from "react-select";
import { useMdmData } from "./useMdmDataChunks"; // adjust the path as needed
import DMGrid from "./dmGrid";
import { LoadingIndicator } from "../../../../lib/loadingIndicator";
import { preferRangeOverSelection } from "../../../../lib/globalAgGrid";
import { toast } from "react-toastify";
import {
  setNextRowToLoad,
  incrementLastId,
  incrementLastIdBy,
  setMdmData,
  duplicateRow,
  updateRowField,
} from "../../reducers/mdmReducer"; // <— We'll create this action

class Option extends Component {
  render() {
    return (
      <div>
        <components.Option {...this.props}>
          <input type="checkbox" checked={this.props.isSelected} onChange={() => null} /> <label>{this.props.value} </label>
        </components.Option>
      </div>
    );
  }
}
const MultiValue = (props) => {
  let labelToBeDisplayed = `${props.data.label} `;
  if (props.selectProps.value.length > 1) {
    labelToBeDisplayed = "multiple selections";
    return (
      <components.MultiValue {...props}>
        <span style={{ fontStyle: "italic" }}>{labelToBeDisplayed}</span>
      </components.MultiValue>
    );
  }

  return (
    <components.MultiValue {...props}>
      <span>{labelToBeDisplayed}</span>
    </components.MultiValue>
  );
};

const ValueContainer = ({ children, ...props }) => {
  if (props.selectProps.value !== null && props.selectProps.value.length > 1) {
    // Only show "multiple selections" text, not each selection
    return <components.ValueContainer {...props}>{[children[0][0], children[1]]}</components.ValueContainer>;
  }
  return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
};

const DMGridContainer = (props) => {
  const {
    mdmConfig,
    activeIdx,
    currentAppIdx,

    setDirty,
    currentTable,
    saveClearKey,
    setRefresh,
    refresh,
    onClearQuickFilters,
    highlightedRowIds,
  } = props;
  const dispatch = useDispatch();

  const reduxRowData = useSelector((state) => state.mdm.data);
  const lastId = useSelector((state) => state.mdm.lastId);
  const [gridApi, setGridApi] = useState(null);
  const [loadedRows, setLoadedRows] = useState(0);
  const [addedRows, setAddedRows] = useState([]);
  const [totalRows, setTotalRows] = useState(mdmConfig[activeIdx].rowCount || 0);
  const [selectedQuickFilters, setSelectedQuickFilters] = useState(null);
  const [quickFilterName, setQuickFilterName] = useState("");
  const [selectedNodesForDelete, setSelectedNodesForDelete] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [placeholderRows, setPlaceholderRows] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [quickFilters, setQuickFilters] = useState([]);
  const rowCountRef = useRef(20);
  // Tracks whether placeholder rows have already been seeded for the current table load.
  // Prevents them from being re-added after a delete or save.
  const placeholdersSeeded = useRef(false);
  const query = useMdmData(mdmConfig[activeIdx].id);
  // const [rowData, setRowData] = useState(query.data && query.data.data ? query.data.data : []);
  const enhancedCurrentTable = useMemo(() => {
    if (!Array.isArray(currentTable)) return [];

    // const seen = new Map();
    return currentTable.map((col) => {
      // let colId = col.colId || col.field; // Use provided colId or fallback to field
      // if (!colId) {
      //   // Generate a unique ID if none exists
      //   colId = `col_${Math.random().toString(36).substr(2, 9)}`;
      // } else {
      //   const originalColId = colId;
      //   let suffix = 1;
      //   while (seen.has(colId)) {
      //     colId = `${originalColId}_${suffix++}`;
      //   }
      // }
      // seen.set(colId, true);

      const isNumeric = col.columnType === "FLOAT"; // Your existing condition (adjust if needed for alignment)

      return {
        ...col,
        // colId: `table-${activeIdx}-${colId}`, // Prefix for extra uniqueness across tables
        cellStyle: isNumeric ? { textAlign: "right" } : undefined,
      };
    });
  }, [currentTable]); // Only recompute if these change

  const clearQuickFilters = useCallback(async () => {
    // Clear the selected filters state
    setSelectedQuickFilters(null);

    // Clear the grid filter model if gridApi is available
    if (gridApi) {
      const tableParamColumn = mdmConfig[activeIdx].data.find((col) => col.tableParameter);
      if (tableParamColumn) {
        const currentFilterModel = await gridApi.getFilterModel();
        const newFilterModel = { ...currentFilterModel };

        // Remove the quick filter from the filter model
        delete newFilterModel[tableParamColumn.colId];

        await gridApi.setFilterModel(newFilterModel);
      }
    }
  }, [gridApi, mdmConfig, activeIdx]);

  useEffect(() => {
    if (onClearQuickFilters) {
      onClearQuickFilters(clearQuickFilters);
    }
  }, [clearQuickFilters, onClearQuickFilters]);
  // Combine only the loaded chunks:
  // const rowData = query.data && query.data.data ? query.data.data : [];
  useEffect(() => {
    // rowData.length is how many rows have been loaded
    dispatch(setNextRowToLoad(reduxRowData.length));
  }, [reduxRowData.length, dispatch]);
  useEffect(() => {
    setLoadedRows(reduxRowData.length);
  }, [reduxRowData.length]);
  useEffect(() => {
    // console.log("THE ACTIVE IDX FOR ROW STUFF " + activeIdx);
    // console.log("THE ROW COUNT " + mdmConfig[activeIdx].rowCount);
    // Use the config value as a placeholder until the query for this table
    // resolves; it can be stale (only updated on the delete-only save path).
    setTotalRows(mdmConfig[activeIdx].rowCount || 0);
  }, [mdmConfig, activeIdx]);
  useEffect(() => {
    // Once the query actually resolves, sync totalRows to the real fetched
    // row count instead of leaving it pinned to the (possibly stale) config
    // value. Without this, renderProgress/placeholder-seeding/the grid-render
    // gate below can hang forever comparing loadedRows against an inflated
    // stale totalRows.
    if (query.isSuccess && query.data) {
      setTotalRows(query.data.rowCount || 0);
    }
  }, [query.isSuccess, query.data]);
  const loadingMore = query.isFetching;
  useEffect(() => {
    // When the active table changes, clear out any placeholder or added rows
    setPlaceholderRows([]);
    setAddedRows([]);
  }, [activeIdx]);
  // Watch for refresh prop to start refreshing
  useEffect(() => {
    if (refresh) {
      setIsRefreshing(true);
      dispatch(setMdmData([], 0, 0));
      placeholdersSeeded.current = false;
      setPlaceholderRows([]);
      setAddedRows([]);
      setRefresh(false);
    }
  }, [refresh, setRefresh, dispatch]);

  // When query finishes fetching new data after refresh, stop loading
  useEffect(() => {
    if (!query.isFetching && isRefreshing) {
      if (query.data && query.data.data) {
        dispatch(setMdmData(query.data.data, query.data.rowCount, 0));
      } else {
        dispatch(setMdmData([], 0, 0));
      }
      setIsRefreshing(false);
    }
  }, [query.isFetching, query.data, isRefreshing, dispatch]);
  // Update rowData when query changes normally (non-refresh)
  useEffect(() => {
    if (!isRefreshing) {
      if (query.data && query.data.data) {
        dispatch(setMdmData(query.data.data, query.data.rowCount, 0));
      } else {
        dispatch(setMdmData([], 0, 0));
      }
    }
  }, [query.data, isRefreshing, dispatch]);
  useEffect(() => {
    const currentTableData = mdmConfig[activeIdx]?.data;
    if (currentTableData) {
      let tableParamColumn = currentTableData.filter((col) => col.tableParameter);
      if (tableParamColumn.length > 0 && tableParamColumn[0].cellEditorParams !== undefined) {
        let paramColumnValues = tableParamColumn[0].cellEditorParams.values.map((f) => ({
          ...f,
          value: f.name,
          label: f.name,
        }));

        setQuickFilterName(tableParamColumn[0].headerName);
        setQuickFilters(paramColumnValues);
      } else {
        if (tableParamColumn.length > 0 && reduxRowData && reduxRowData.length > 0) {
          const field = tableParamColumn[0].field;
          // Extract unique values from reduxRowData for the specified field
          const uniqueValues = [
            ...new Set(reduxRowData.map((row) => row[field]).filter((value) => value !== undefined && value !== null)),
          ].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
          // Format values as { value, label } objects
          const paramColumnValues = uniqueValues.map((value) => ({
            value,
            label: value,
          }));

          setQuickFilterName(tableParamColumn[0].headerName);
          setQuickFilters(paramColumnValues);
        }
      }
    }
  }, [mdmConfig, activeIdx, reduxRowData]);
  // const isLoading = queries.some(q => q.isLoading || q.isFetching);
  // If all data is loaded, add placeholders once
  useEffect(() => {
    // Only add placeholder rows if we have loaded some regular rows,
    // and only when all regular rows have been loaded,
    // and no placeholders have been added yet.
    if (reduxRowData.length > 0 && reduxRowData.length === totalRows && !loadingMore && !placeholdersSeeded.current) {
      placeholdersSeeded.current = true;
      addPlaceholderRows();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxRowData.length, totalRows, loadingMore, placeholderRows.length]);
  // Fires once per completed save (saveClearKey is bumped exactly once, after the
  // response — success, partial, or otherwise — has been fully processed and
  // highlightedRowIds set to its final value for that save; see saveHandler in
  // index.js). Drop every placeholder/added row that was actually submitted and did
  // NOT fail: it's now a real, server-backed row that will come back through the
  // normal data refetch, so keeping the local copy around (still flagged
  // crudType "insert"/"update") would resubmit it — a genuine duplicate insert, or
  // at best a spurious "duplicate key" rejection — the next time the user saves
  // anything else in this session. Untouched ("empty") placeholders and rows the
  // server actually rejected (still in highlightedRowIds) are kept: the former have
  // nothing to drop, the latter still need to be visible so the user can fix them.
  //
  // Deliberately NOT depending on highlightedRowIds: it's cleared to [] the instant
  // a new save starts (see saveHandler), well before saveClearKey changes — if this
  // effect re-ran for that, it would find no failed ids yet and prune every
  // currently-submitted row out from under the in-flight request.
  useEffect(() => {
    const failedIds = new Set((highlightedRowIds || []).map(String));
    const keepAfterSave = (row) => row.crudType === "empty" || failedIds.has(String(row.ID));
    setPlaceholderRows((prev) => prev.filter(keepAfterSave));
    setAddedRows((prev) => prev.filter(keepAfterSave));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveClearKey]);

  useEffect(() => {
    // When the active table changes, clear out any placeholder or added rows
    // and reset the seeded flag so the new table gets its own fresh placeholders.
    placeholdersSeeded.current = false;
    setPlaceholderRows([]);
    setAddedRows([]);
    // Also clear quick filters when switching tables
    setSelectedQuickFilters(null);
    setQuickFilters([]);
    setQuickFilterName("");
  }, [activeIdx]);

  // Check overall loading/error state
  // const isLoading = queries.some(q => q.isLoading);
  const isError = query.isError;
  const error = query.error;

  // **** Begin New Filter Code - moved before early return to satisfy React Hooks rules
  const applyQuickFilters = useCallback(
    async (selectedItems) => {
      if (!gridApi || !selectedItems || selectedItems.length === 0) return;

      const tableParamColumn = mdmConfig[activeIdx].data.find((col) => col.tableParameter);
      if (!tableParamColumn) {
        console.error("Table parameter column not found.");
        return;
      }

      const selectedValues = selectedItems.map((item) => item.value);
      const currentFilterModel = await gridApi.getFilterModel();
      const newFilterModel = { ...currentFilterModel };

      if (selectedValues.length > 0) {
        newFilterModel[tableParamColumn.colId] = {
          filterType: "set", // correct property name
          values: selectedValues,
        };
      } else {
        delete newFilterModel[tableParamColumn.colId]; // Remove the filter if no values are selected
      }

      await gridApi.setFilterModel(newFilterModel);
    },
    [gridApi, mdmConfig, activeIdx],
  );

  useEffect(() => {
    // Apply filters when grid API becomes available and we have selected filters
    if (gridApi && selectedQuickFilters && selectedQuickFilters.length > 0) {
      applyQuickFilters(selectedQuickFilters);
    }
  }, [gridApi, selectedQuickFilters, applyQuickFilters]);
  // **** End New Filter Code

  // Utility function to format date
  function formatDateTime(date) {
    if (!date || !(date instanceof Date) || isNaN(date)) {
      return "N/A";
    }

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const hoursStr = String(hours).padStart(2, "0");

    return `${month}/${day}/${year} ${hoursStr}:${minutes} ${ampm}`;
  }
  function addPlaceholderRows() {
    // Suppose you want 20 placeholders
    const newRows = [];
    // Compute the true max ID over all loaded rows — the server's row order isn't
    // guaranteed to reflect insertion/ID order (no ORDER BY on the underlying
    // query), so the highest ID isn't necessarily the last element.
    const localLastId = reduxRowData.reduce((max, row) => Math.max(max, row.ID), 0);
    for (let i = 1; i <= 20; i++) {
      newRows.push({
        ID: localLastId + i,
        crudType: "empty",
        // any other columns
      });
      dispatch(incrementLastId());
    }
    // console.log("NEW ROWS");
    // console.log(newRows);
    if (mdmConfig[activeIdx].allowNewRows) {
      setPlaceholderRows(newRows);
    }
  }

  function addNewRow({ row, index }) {
    const newData = cloneDeep(row);
    newData.ID = lastId + 1; // Set to lastId + 1 (not null) for unique ID in grid; server will assign real ID on save
    newData.crudType = "insert";

    // Insert the new row into Redux data at the specified index
    dispatch(duplicateRow({ row: newData, index }));

    dispatch(incrementLastId());
    setDirty(true);
  }

  if (isError) return <div>Error: {error.message}</div>;
  const finalData = [...reduxRowData, ...placeholderRows, ...addedRows];
  // setFinalData([...reduxRowData, ...placeholderRows, ...addedRows]);
  function addRow() {
    // If there's at least one row of data, clone that
    // Otherwise, build a brand-new object from `currentTable`
    let newData = null;

    if (finalData.length > 0) {
      newData = cloneDeep(finalData[0]);
      // zero out anything except ID
      Object.keys(newData).forEach((key) => {
        if (key === "ID") {
          newData[key] = lastId + 1; // lastId from props
        } else {
          newData[key] = null;
        }
      });
    } else if (currentTable?.length > 0) {
      // build from the column definitions
      newData = {};
      for (const col of currentTable) {
        if (col.headerName === "ID") {
          newData[col.headerName] = 1;
        } else {
          newData[col.headerName] = null;
        }
      }
    } else {
      // fallback: just create a minimal object
      newData = { ID: lastId + 1 };
    }

    // Pre-populate fields with current filter values so new row appears in filtered view
    if (gridApi) {
      const filterModel = gridApi.getFilterModel();
      console.log("[addRow] Current filter model:", filterModel);
      if (filterModel && Object.keys(filterModel).length > 0) {
        // Get column definitions to map colId to field
        const columnDefs = mdmConfig[activeIdx].data || [];
        console.log(
          "[addRow] Column defs:",
          columnDefs.map((c) => ({ colId: c.colId, field: c.field })),
        );

        Object.keys(filterModel).forEach((colId) => {
          const filter = filterModel[colId];
          // Find the column definition that matches this colId
          // Try matching as string and as number since colId from filter model is a string
          const colDef = columnDefs.find((col) => {
            return String(col.colId) === String(colId) || col.field === colId;
          });
          const fieldName = colDef ? colDef.field : colId;

          console.log(
            "[addRow] Processing filter for colId:",
            colId,
            "found colDef:",
            colDef,
            "fieldName:",
            fieldName,
            "filter:",
            filter,
          );

          // Handle set filters (values array)
          if (filter.values && filter.values.length === 1) {
            // If filtering on a single value, pre-populate the new row with that value
            console.log("[addRow] Setting", fieldName, "to", filter.values[0]);
            newData[fieldName] = filter.values[0];
          } else if (filter.filter !== undefined) {
            // Handle text/number filters with a single value
            console.log("[addRow] Setting", fieldName, "to", filter.filter);
            newData[fieldName] = filter.filter;
          }
        });
      }
    }

    // Also apply quick filters if selected (tableParameter column)
    if (selectedQuickFilters && selectedQuickFilters.length === 1) {
      const tableParamColumn = mdmConfig[activeIdx].data.find((col) => col.tableParameter);
      if (tableParamColumn) {
        console.log("[addRow] Applying quick filter:", tableParamColumn.field, "=", selectedQuickFilters[0].value);
        newData[tableParamColumn.field] = selectedQuickFilters[0].value;
      }
    }

    console.log("[addRow] Final newData:", newData);

    // "empty", not "insert": a filter/quick-filter value above may have pre-populated
    // one field for display purposes, but that alone isn't user intent to save this
    // row. cellValueChanged (dmGrid.js) promotes it to "insert" the moment the user
    // actually edits a cell — matching how placeholder rows already behave. Marking
    // it "insert" here unconditionally would let an untouched, filter-prepopulated
    // row pass saveHandler's "not entirely blank" check and get inserted for real.
    newData.crudType = "empty";

    setAddedRows((prev) => [...prev, newData]);

    // Mark ourselves as "dirty"
    setDirty(true);

    // Increment lastId in Redux store to ensure next row gets unique ID
    dispatch(incrementLastId());
  }

  function addRows(count) {
    const newRows = [];

    // Resolve filter values once for all rows
    let filterValues = {};
    if (gridApi) {
      const filterModel = gridApi.getFilterModel();
      if (filterModel && Object.keys(filterModel).length > 0) {
        const columnDefs = mdmConfig[activeIdx].data || [];
        Object.keys(filterModel).forEach((colId) => {
          const filter = filterModel[colId];
          const colDef = columnDefs.find((col) => String(col.colId) === String(colId) || col.field === colId);
          const fieldName = colDef ? colDef.field : colId;
          if (filter.values && filter.values.length === 1) {
            filterValues[fieldName] = filter.values[0];
          } else if (filter.filter !== undefined) {
            filterValues[fieldName] = filter.filter;
          }
        });
      }
    }

    let quickFilterField = null;
    let quickFilterValue = null;
    if (selectedQuickFilters && selectedQuickFilters.length === 1) {
      const tableParamColumn = mdmConfig[activeIdx].data.find((col) => col.tableParameter);
      if (tableParamColumn) {
        quickFilterField = tableParamColumn.field;
        quickFilterValue = selectedQuickFilters[0].value;
      }
    }

    for (let i = 0; i < count; i++) {
      let newData = null;
      const newId = lastId + i + 1;

      if (finalData.length > 0) {
        newData = cloneDeep(finalData[0]);
        Object.keys(newData).forEach((key) => {
          newData[key] = key === "ID" ? newId : null;
        });
      } else if (currentTable?.length > 0) {
        newData = {};
        for (const col of currentTable) {
          newData[col.headerName] = col.headerName === "ID" ? newId : null;
        }
      } else {
        newData = { ID: newId };
      }

      Object.assign(newData, filterValues);
      if (quickFilterField !== null) {
        newData[quickFilterField] = quickFilterValue;
      }

      // See addRow()'s comment: stays "empty" until the user actually edits a cell,
      // so untouched rows (including ones a filter pre-populated one field on) never
      // reach the server as real inserts.
      newData.crudType = "empty";
      newRows.push(newData);
    }

    setAddedRows((prev) => [...prev, ...newRows]);
    setDirty(true);
    dispatch(incrementLastIdBy(count));
    toast.success(`${count} row${count === 1 ? "" : "s"} added`);
  }

  function addSingleRowForKeyboard(gridApi, editableCols) {
    if (!mdmConfig[activeIdx].allowNewRows) return;
    addRows(1);
    // Wait for React to re-render and AG Grid to reconcile the new row,
    // then start editing its first editable cell.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          const lastRowIndex = gridApi.getDisplayedRowCount() - 1;
          if (lastRowIndex >= 0 && editableCols.length > 0) {
            gridApi.startEditingCell({
              rowIndex: lastRowIndex,
              colKey: editableCols[0].getColId(),
            });
          }
        } catch (e) {}
      });
    });
  }

  async function quickFilterClick(selectedItems) {
    // console.log("CLICKING " + selectedItems);
    if (selectedItems.length === 0) {
      await gridApi.setFilterModel({});
    }
    await applyQuickFilters(selectedItems);
    setSelectedQuickFilters(selectedItems);
  }

  // **** End New Filter Code

  // async function quickFilterClick(selectedItems) {
  //   console.log("CLICKING " + selectedItems);
  //   const tableParamColumn = mdmConfig[activeIdx].data.find((col) => col.tableParameter);
  //   if (!tableParamColumn) {
  //     console.error("Table parameter column not found.");
  //     return;
  //   }
  //   // const field = tableParamColumn.field;
  //   const selectedValues = selectedItems.map((item) => item.value);

  //   if (gridApi) {
  //     const currentFilterModel = await gridApi.getFilterModel();

  //     const newFilterModel = { ...currentFilterModel };

  //     if (selectedValues.length > 0) {
  //       newFilterModel[tableParamColumn.colId] = {
  //         filterType: "set", // correct property name
  //         values: selectedValues,
  //       };
  //     } else {
  //       delete newFilterModel[tableParamColumn.colId]; // Remove the filter if no values are selected
  //     }

  //     await gridApi.setFilterModel(newFilterModel);
  //   }

  //   setSelectedQuickFilters(selectedItems);
  //   // No need for setRefresh(true) since we're applying the filter directly
  // }
  function deleteModal() {
    setShowDeleteConfirmModal(true);
  }
  // A small local progress display
  function renderProgress() {
    // console.log("TOTAL ROWS " + totalRows);
    if (loadedRows >= totalRows && totalRows > 0) {
      return <div style={{ marginRight: "30px" }}>{`Completed ${totalRows} Rows`}</div>;
    } else if (loadedRows > 0) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            marginLeft: "auto",
            marginRight: "30px",
          }}>
          <DotLoader size={15} color="rgba(0, 126, 167, 1)" loading />
          <div
            style={{
              color: "rgba(0, 126, 167, 1)",
              fontStyle: "italic",
              marginLeft: "5px",
            }}>
            {`Loading ${loadedRows}/${totalRows} Rows`}
          </div>
        </div>
      );
    }
    return null;
  }
  // console.log("FINAL DATA");
  // console.log(finalData);
  if (isError) return <div>Error: {error.message}</div>;
  if (isRefreshing || (finalData.length === 0 && loadingMore)) {
    // Show loading spinner while refreshing
    return <LoadingIndicator />;
  }

  if (!reduxRowData || reduxRowData.length === 0) {
    // A table can legitimately have zero rows. Only treat an empty result as
    // "still loading" while the query is actually in flight (isRefreshing/
    // loadingMore); once it has resolved successfully with zero rows, show an
    // empty state instead of spinning on <LoadingIndicator /> forever.
    // Use the freshly fetched row count (query.data.rowCount) rather than the
    // config-derived totalRows, which is only updated on a stale delete-only
    // save path and can remain nonzero after a refetch/refresh legitimately
    // returns zero rows, causing this to fall through to LoadingIndicator forever.
    const fetchedRowCount = query.data ? query.data.rowCount : undefined;
    const isConfirmedEmpty = !isRefreshing && !loadingMore && query.isSuccess && fetchedRowCount === 0;
    if (isConfirmedEmpty) {
      return <div style={{ padding: "40px", textAlign: "center", color: "#888" }}>No rows to display</div>;
    }
    return <LoadingIndicator />;
  }
  if (isRefreshing || loadingMore || finalData.length === 0 || loadedRows < totalRows) {
    return <LoadingIndicator />; // don’t render the grid yet
  }

  // const loadedRowCount = reduxRowData.length;
  // const progress = totalRows > 0 ? (loadedRowCount / totalRows) * 100 : 0;
  function handleClose() {
    let idx = parseInt(activeIdx, 10);
    props.history.push(`/mdm/dataManagement/${currentAppIdx}+${idx}`);
    setShowDeleteConfirmModal(false);
    setSelectedNodesForDelete([]);
  }
  function handleCellValueChanged(item) {
    dispatch(updateRowField(item.data.ID, item.colDef.field, item.newValue));
    setDirty(true);
  }
  async function handleGridReady(params) {
    // store the AG Grid api in state or a ref

    // this.agGrid.current.gridApi;
    setGridApi(params.current.gridApi);

    if (props && props.parentGridReady) {
      props.parentGridReady(params);
    }
  }
  function checkRemove() {
    // Build sets of IDs that are purely client-side (never sent to the server).
    // A placeholder row starts as crudType "empty" but becomes "insert" once edited —
    // so we match by ID against both placeholderRows and addedRows, not by crudType alone.
    const placeholderIds = new Set(placeholderRows.map((r) => r.ID));
    const addedIds = new Set(addedRows.map((r) => r.ID));

    const clientOnlyRows = selectedNodesForDelete.filter(
      (n) => placeholderIds.has(n.data?.ID) || addedIds.has(n.data?.ID),
    );
    const serverRows = selectedNodesForDelete.filter(
      (n) => !placeholderIds.has(n.data?.ID) && !addedIds.has(n.data?.ID),
    );

    if (clientOnlyRows.length > 0) {
      const clientIds = new Set(clientOnlyRows.map((n) => n.data.ID));
      setPlaceholderRows((prev) => prev.filter((r) => !clientIds.has(r.ID)));
      setAddedRows((prev) => prev.filter((r) => !clientIds.has(r.ID)));
    }

    // Send persisted rows through the normal delete + save path.
    if (serverRows.length > 0) {
      props.removeRows(serverRows);
    }

    handleClose();
  }
  function checkRemoveStatus() {
    const toDeleteItems = (nodes) => nodes.map((node) => ({ rowId: node.rowIndex, data: node.data }));
    let selectedNodes = gridApi.getSelectedNodes();

    // A click-and-drag across cells (e.g. from the first cell of a row down through
    // several rows) builds a cell range, not row selection — and a plain click always
    // row-selects the clicked row as a side effect, so getSelectedNodes() alone only
    // ever reflects that one anchor row, never the rest of the drag. Prefer the cell
    // range's rows when it covers more than the current row selection (same fix as
    // globalAgGrid.js's copyRow).
    if (preferRangeOverSelection(gridApi, selectedNodes.length)) {
      selectedNodes = gridApi.getSelectedNodes();
    }

    if (selectedNodes.length > 0) {
      setSelectedNodesForDelete(toDeleteItems(selectedNodes));
      return true;
    }
    return false;
  }
  function renderLastAction() {
    if (
      props.lastRunAction !== undefined &&
      props.lastRunAction.ACTION_GROUP_NAME &&
      props.lastRunAction.LAST_RUN_TIME &&
      props.lastRunAction.LAST_RUN_BY &&
      props.lastRunAction.error === undefined
    ) {
      return (
        <div
          style={{
            fontFamily: "'Arial', sans-serif",
            fontSize: "14px",
            color: "#000000",
            padding: "10px 0",
            marginBottom: "10px",
            display: "flex",
            flexDirection: "row",
          }}>
          Last Action:{" "}
          <div style={{ fontWeight: "bold", fontStyle: "italic", padding: "0 5px" }}>
            {props.lastRunAction.ACTION_GROUP_NAME}
          </div>{" "}
          on {formatDateTime(new Date(props.lastRunAction.LAST_RUN_TIME))} by {props.lastRunAction.LAST_RUN_BY}
        </div>
      );
    } else {
      return null;
    }
  }
  // let pathInfo = this.props.location.pathname.split("/")[3];
  // const currentTableId = this.props.location.pathname.split("/")[3] ? pathInfo.split("+")[1] : 0;

  const allowAddDelete = mdmConfig[activeIdx].allowNewRows;
  // Pass reduxRowData into DMGrid as a prop (you can also pass a callback if needed)
  return (
    <>
      <Modal isOpen={showDeleteConfirmModal}>
        <ModalHeader>Delete Row</ModalHeader>
        <ModalBody>
          <div>
            Are you sure you want to delete the {selectedNodesForDelete.length}{" "}
            selected {selectedNodesForDelete.length === 1 ? "row" : "rows"}? This cannot be undone.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              checkRemove();
            }}>
            Delete
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </ModalFooter>
      </Modal>
      <div style={{ display: "flex", flexDirection: "row" }}>
        {/* Quick Filters */}
        <div>
          {quickFilters.length > 0 && (
            <div style={{ marginTop: "10px", marginRight: "20px", display: "flex", flexDirection: "row" }}>
              <div>{quickFilterName} Filter:</div>
              <div>
                <Select
                  className="quick-filter-dropdown"
                  classNamePrefix="react-select-qf"
                  placeholder="none"
                  value={selectedQuickFilters}
                  onChange={(e) => quickFilterClick(e)}
                  options={quickFilters}
                  closeMenuOnSelect={false}
                  isMulti
                  components={{ Option, MultiValue, ValueContainer }}
                  hideSelectedOptions={false}
                  backspaceRemovesValue={false}
                  isClearable={false}
                />
              </div>
            </div>
          )}
        </div>
        {renderLastAction()}
        <div
          className="pull-right action-buttons"
          style={{ display: "inline-flex", marginLeft: "auto", marginBottom: "10px", marginTop: "10px" }}>
          {renderProgress()}
          {allowAddDelete && (
            <>
              <div
                style={{ marginLeft: "auto", marginRight: "20px", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <div
                  onClick={() => {
                    if (mdmConfig[activeIdx].allowNewRows) addRows(rowCountRef.current);
                  }}
                  className={"mdm-action-button " + (mdmConfig[activeIdx].allowNewRows ? "" : "disabled")}
                  style={{ whiteSpace: "nowrap", marginLeft: 0, marginRight: 0 }}>
                  Add Rows:
                </div>
                <input
                  type="number"
                  min={1}
                  defaultValue={20}
                  className="mdm-row-count-input"
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    rowCountRef.current = Math.max(1, parseInt(e.target.value) || 1);
                  }}
                  style={{
                    width: "52px",
                    textAlign: "right",
                    border: "1px solid #ccc",
                    borderRadius: "3px",
                    padding: "2px 4px",
                    fontSize: "13px",
                  }}
                />
              </div>
              <div
                style={{ width: "100px" }}
                onClick={() => {
                  if (mdmConfig[activeIdx].allowNewRows && checkRemoveStatus()) {
                    deleteModal();
                  }
                }}
                className={"mdm-action-button " + (mdmConfig[activeIdx].allowNewRows ? "" : "disabled")}>
                Delete Row
              </div>
            </>
          )}
        </div>
      </div>
      <DMGrid
        {...props}
        key={`dmgrid-${mdmConfig[activeIdx].id}-${activeIdx}`}
        loadingMore={loadingMore}
        currentAppIdx={props.currentAppIdx}
        rowData={finalData}
        gridReady={handleGridReady}
        onCellValueChanged={handleCellValueChanged}
        filterDidChange={(filterColumn, filterValues) => {
          // console.log("DOING STUFF FOR FILTER CHANGE");
          // console.log("@@@@@@@@@@@@FILTER COLUMN ");
          // console.log(filterColumn);
          // console.log(filterValues);

          if (filterColumn?.context?.tableParameter && filterValues.values.length > 0) {
            let newFilters = filterValues.values.map((i) => {
              return { value: i, label: i };
            });

            setSelectedQuickFilters(newFilters);
          } else {
            setSelectedQuickFilters(null);
          }
        }}
        addNewRow={addNewRow}
        onAddRowForKeyboard={addSingleRowForKeyboard}
        currentTable={enhancedCurrentTable}
      />
    </>
  );
};

export default DMGridContainer;
